"use client";

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { SearchInput } from "@/components/ui/SearchInput";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatCurrency } from "@/lib/utils";
import { QuickLinks } from "@/components/ui/QuickLinks";

interface LoyaltyAccountData {
  id: string;
  pointsBalance: number;
  lifetimePoints: number;
  tier: "BRONZE" | "SILVER" | "GOLD" | "PLATINUM" | "VIP";
}

interface CustomerData {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  allergies: string | null;
  preferences: string | null;
  totalVisits: number;
  totalSpent: number;
  lastVisit: string | null;
  loyaltyAccount: LoyaltyAccountData | null;
  createdAt: string;
  updatedAt: string;
}

const TIER_CONFIG: Record<
  LoyaltyAccountData["tier"],
  { variant: "default" | "success" | "warning" | "danger" | "info" }
> = {
  BRONZE: { variant: "default" },
  SILVER: { variant: "info" },
  GOLD: { variant: "warning" },
  PLATINUM: { variant: "success" },
  VIP: { variant: "danger" },
};

export default function CustomersPage() {
  const [customers, setCustomers] = useState<CustomerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerData | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add form state
  const [formFirstName, setFormFirstName] = useState("");
  const [formLastName, setFormLastName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formAllergies, setFormAllergies] = useState("");
  const [formPreferences, setFormPreferences] = useState("");

  // Edit form state
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editAllergies, setEditAllergies] = useState("");
  const [editPreferences, setEditPreferences] = useState("");

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      params.set("page", String(page));
      params.set("limit", "20");
      const res = await fetch(`/api/customers?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch customers");
      const json = await res.json();
      setCustomers(json.data);
      setTotalPages(json.totalPages);
      setTotal(json.total);
    } catch {
      setError("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const resetForm = () => {
    setFormFirstName("");
    setFormLastName("");
    setFormEmail("");
    setFormPhone("");
    setFormNotes("");
    setFormAllergies("");
    setFormPreferences("");
  };

  const handleAdd = async () => {
    if (!formFirstName.trim()) {
      setError("First name is required");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch("/api/customers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: formFirstName.trim(),
          lastName: formLastName.trim() || undefined,
          email: formEmail.trim() || undefined,
          phone: formPhone.trim() || undefined,
          notes: formNotes.trim() || undefined,
          allergies: formAllergies.trim() || undefined,
          preferences: formPreferences.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || "Failed to create customer");
      }

      setShowAddModal(false);
      resetForm();
      await fetchCustomers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create customer");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDetail = (customer: CustomerData) => {
    setSelectedCustomer(customer);
    setEditFirstName(customer.firstName);
    setEditLastName(customer.lastName || "");
    setEditEmail(customer.email || "");
    setEditPhone(customer.phone || "");
    setEditNotes(customer.notes || "");
    setEditAllergies(customer.allergies || "");
    setEditPreferences(customer.preferences || "");
    setShowDetailModal(true);
    setError(null);
  };

  const handleUpdate = async () => {
    if (!selectedCustomer) return;
    if (!editFirstName.trim()) {
      setError("First name is required");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(`/api/customers/${selectedCustomer.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: editFirstName.trim(),
          lastName: editLastName.trim() || undefined,
          email: editEmail.trim() || undefined,
          phone: editPhone.trim() || undefined,
          notes: editNotes.trim() || undefined,
          allergies: editAllergies.trim() || undefined,
          preferences: editPreferences.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || "Failed to update customer");
      }

      setShowDetailModal(false);
      setSelectedCustomer(null);
      await fetchCustomers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update customer");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCustomer) return;

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(`/api/customers/${selectedCustomer.id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || "Failed to delete customer");
      }

      setShowDetailModal(false);
      setSelectedCustomer(null);
      await fetchCustomers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete customer");
    } finally {
      setSaving(false);
    }
  };

  const getFullName = (customer: CustomerData) => {
    return customer.lastName
      ? `${customer.firstName} ${customer.lastName}`
      : customer.firstName;
  };

  const getInitials = (customer: CustomerData) => {
    const first = customer.firstName[0] || "";
    const last = customer.lastName?.[0] || "";
    return (first + last).toUpperCase();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Customers</h1>
          <p className="mt-1 text-text-muted">
            Customer profiles and loyalty tracking
            {total > 0 && (
              <span className="ml-2 text-text-dim">({total} total)</span>
            )}
          </p>
          <div className="mt-3">
            <QuickLinks links={[
              { label: "Profiles", href: "/dashboard/customers" },
              { label: "Reservations", href: "/dashboard/reservations" },
            ]} />
          </div>
        </div>
        <Button onClick={() => { setShowAddModal(true); setError(null); resetForm(); }}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add Customer
        </Button>
      </div>

      {/* Search */}
      <SearchInput
        onSearch={handleSearch}
        placeholder="Search by name, email, or phone..."
      />

      {/* Error banner */}
      {error && !showAddModal && !showDetailModal && (
        <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : customers.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              title={search ? "No customers found" : "No customers yet"}
              description={
                search
                  ? "Try adjusting your search terms."
                  : "Customer profiles will be created automatically from orders, or you can add them manually."
              }
              icon={
                <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              }
              action={
                !search
                  ? { label: "Add Customer", onClick: () => { setShowAddModal(true); resetForm(); } }
                  : undefined
              }
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {customers.map((customer) => {
              const tierCfg = customer.loyaltyAccount
                ? TIER_CONFIG[customer.loyaltyAccount.tier]
                : null;
              return (
                <Card
                  key={customer.id}
                  hover
                  className="cursor-pointer"
                  onClick={() => handleOpenDetail(customer)}
                >
                  <CardContent className="flex items-center gap-4">
                    {/* Avatar */}
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent/15 text-sm font-semibold text-accent">
                      {getInitials(customer)}
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium text-white">
                          {getFullName(customer)}
                        </p>
                        {customer.loyaltyAccount && tierCfg && (
                          <Badge variant={tierCfg.variant} size="sm">
                            {customer.loyaltyAccount.tier}
                          </Badge>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-text-muted">
                        {customer.email && (
                          <span className="truncate">{customer.email}</span>
                        )}
                        {customer.phone && (
                          <span>{customer.phone}</span>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="hidden items-center gap-6 sm:flex">
                      <div className="text-right">
                        <p className="text-xs text-text-dim">Visits</p>
                        <p className="text-sm font-medium text-white">{customer.totalVisits}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-text-dim">Spent</p>
                        <p className="text-sm font-medium text-white">
                          {formatCurrency(customer.totalSpent)}
                        </p>
                      </div>
                      {customer.loyaltyAccount && (
                        <div className="text-right">
                          <p className="text-xs text-text-dim">Points</p>
                          <p className="text-sm font-medium text-accent">
                            {customer.loyaltyAccount.pointsBalance.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Chevron */}
                    <svg className="h-4 w-4 shrink-0 text-text-dim" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-text-muted">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Add Customer Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Customer"
        size="lg"
      >
        <div className="space-y-4">
          {error && (
            <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
              {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name"
              value={formFirstName}
              onChange={(e) => setFormFirstName(e.target.value)}
              placeholder="John"
            />
            <Input
              label="Last Name"
              value={formLastName}
              onChange={(e) => setFormLastName(e.target.value)}
              placeholder="Doe"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              placeholder="john@example.com"
            />
            <Input
              label="Phone"
              type="tel"
              value={formPhone}
              onChange={(e) => setFormPhone(e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>
          <Input
            label="Allergies"
            value={formAllergies}
            onChange={(e) => setFormAllergies(e.target.value)}
            placeholder="e.g. Peanuts, Shellfish"
          />
          <Input
            label="Preferences"
            value={formPreferences}
            onChange={(e) => setFormPreferences(e.target.value)}
            placeholder="e.g. Window seat, no ice"
          />
          <Textarea
            label="Notes"
            value={formNotes}
            onChange={(e) => setFormNotes(e.target.value)}
            placeholder="Any additional notes..."
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} loading={saving}>
              Create Customer
            </Button>
          </div>
        </div>
      </Modal>

      {/* Detail/Edit Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedCustomer(null); }}
        title={selectedCustomer ? getFullName(selectedCustomer) : "Customer Details"}
        size="lg"
      >
        {selectedCustomer && (
          <div className="space-y-4">
            {error && (
              <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
                {error}
              </div>
            )}

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-white/5 p-3 text-center">
                <p className="text-xs text-text-dim">Total Visits</p>
                <p className="mt-1 text-lg font-semibold text-white">{selectedCustomer.totalVisits}</p>
              </div>
              <div className="rounded-xl bg-white/5 p-3 text-center">
                <p className="text-xs text-text-dim">Total Spent</p>
                <p className="mt-1 text-lg font-semibold text-white">{formatCurrency(selectedCustomer.totalSpent)}</p>
              </div>
              <div className="rounded-xl bg-white/5 p-3 text-center">
                <p className="text-xs text-text-dim">Loyalty Tier</p>
                <p className="mt-1 text-lg font-semibold text-accent">
                  {selectedCustomer.loyaltyAccount?.tier || "None"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                value={editFirstName}
                onChange={(e) => setEditFirstName(e.target.value)}
              />
              <Input
                label="Last Name"
                value={editLastName}
                onChange={(e) => setEditLastName(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Email"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
              />
              <Input
                label="Phone"
                type="tel"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
              />
            </div>
            <Input
              label="Allergies"
              value={editAllergies}
              onChange={(e) => setEditAllergies(e.target.value)}
            />
            <Input
              label="Preferences"
              value={editPreferences}
              onChange={(e) => setEditPreferences(e.target.value)}
            />
            <Textarea
              label="Notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
            />
            <div className="flex items-center justify-between pt-2">
              <Button variant="danger" size="sm" onClick={handleDelete} loading={saving}>
                Delete
              </Button>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={() => { setShowDetailModal(false); setSelectedCustomer(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleUpdate} loading={saving}>
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
